import { registerLocaleVariant } from "../registry";
import { makeId } from "../helpers";
import type { ArticleBody } from "../types";

// ─── Helper ─────────────────────────────────────────────────────────

function frMoonArticle(
  slug: string,
  variant: {
    title: string;
    subtitle: string;
    body: ArticleBody;
  },
): void {
  registerLocaleVariant(makeId(`moon-article-${slug}`), "fr", variant);
}

// ─── Articles ───────────────────────────────────────────────────────

frMoonArticle("embryo-grading", {
  title: "Décoder les notes de tes embryons",
  subtitle:
    "Apprendre à lire 4AA, 3BB, et les autres notes que ta clinique va te donner.",
  body: {
    introduction: "Pour commencer",
    quickSummary: [
      {
        title: "La classification est subjective.",
        description:
          "Elle décrit l'apparence, pas le destin. Les embryologistes évaluent la structure au microscope, mais ce n'est pas une science exacte.",
      },
      {
        title: "Un grade idéal ne garantit pas une grossesse.",
        description:
          "Et un grade « plus bas » ne signifie pas un échec. Beaucoup de bébés en bonne santé viennent d'embryons qui n'étaient pas parfaits sur le papier.",
      },
      {
        title: "Le PGT-A et la classification mesurent des choses différentes.",
        description:
          "Le PGT-A te renseigne sur les chromosomes ; la classification, sur la structure. Les deux sont utiles, mais répondent à des questions différentes.",
      },
    ],
    sections: [
      {
        title: "Classification au jour 3",
        content:
          "Au jour 3 après la fécondation, les embryons sont au stade de clivage — généralement 6 à 8 cellules. Les embryologistes évaluent deux facteurs principaux : le nombre de cellules et la fragmentation. Un embryon idéal au jour 3 a 7 à 8 cellules de taille uniforme avec une fragmentation minimale (débris cellulaires entre les cellules).",
        bulletPoints: [
          "Nombre de cellules : 6 à 8 est l'idéal au jour 3",
          "Fragmentation : notée en pourcentage — moins de 10 % est excellent",
          "Symétrie : des cellules de taille uniforme sont préférables",
          "La plupart des cliniques cultivent maintenant jusqu'au jour 5 plutôt que de transférer au jour 3",
        ],
      },
      {
        title: "Classification du blastocyste au jour 5 / 6",
        content:
          "Au jour 5 ou 6, un embryon en bonne santé est devenu un blastocyste — une structure remplie de liquide avec deux groupes de cellules distincts. La classification du blastocyste utilise un système à trois parties : un chiffre pour l'expansion (1 à 6), une lettre pour la masse cellulaire interne ou MCI (A à C), et une lettre pour le trophectoderme ou TE (A à C).",
        bulletPoints: [
          "Chiffre d'expansion (1 à 6) : à quel point le blastocyste est dilaté, 3 et plus étant complètement dilaté",
          "Masse cellulaire interne (A à C) : l'amas de cellules qui devient le bébé",
          "Trophectoderme (A à C) : la couronne externe de cellules qui devient le placenta",
          "A = beaucoup de cellules serrées, B = modéré, C = peu de cellules ou regroupées de façon lâche",
        ],
      },
      {
        title: "Ce que « 4AA » veut vraiment dire",
        content:
          "Une note de 4AA signifie que le blastocyste est complètement dilaté (4), avec une masse cellulaire interne bien compacte (A) et un trophectoderme bien organisé (A). C'est considéré comme un embryon de qualité supérieure — mais ce n'est pas la seule note qui mène à des grossesses en bonne santé. Des embryons notés 3BB, 4AB, ou même 4BC donnent régulièrement des naissances vivantes.",
      },
      {
        title:
          "Pourquoi les notes ne sont PAS les mêmes d'une clinique à l'autre",
        content:
          "Il n'existe pas de standard universel de classification. Le 4AA d'une clinique peut être le 3AB d'une autre. Des embryologistes différents peuvent classer le même embryon différemment, et certaines cliniques utilisent des systèmes de notation totalement différents. C'est pourquoi comparer les notes entre cliniques n'est pas fiable.",
        bulletPoints: [
          "Aucun standard universel de classification n'existe",
          "Certaines cliniques utilisent des systèmes numériques au lieu de lettres",
          "Le même embryon peut recevoir des notes différentes selon les embryologistes",
          "Demande à ta clinique d'expliquer son barème de classification",
        ],
      },
      {
        title: "Classification vs. résultats PGT-A",
        content:
          "La classification et le PGT-A répondent à des questions fondamentalement différentes. La classification évalue l'apparence physique et la structure de l'embryon. Le PGT-A vérifie si l'embryon a le bon nombre de chromosomes. Un embryon magnifiquement noté 5AA peut être aneuploïde (chromosomes anormaux), et un 3BB d'apparence modeste peut être euploïde (chromosomes normaux).",
      },
      {
        title: "Quelles notes comptent pour les taux de réussite",
        content:
          "Les embryons mieux notés ont statistiquement de meilleurs taux d'implantation, mais la corrélation n'est pas absolue. Le facteur le plus important pour la réussite est de savoir si l'embryon est chromosomiquement normal. Le PGT-A (test des aneuploïdies) permet de le vérifier, mais en France il n'est pas autorisé en routine. Le DPI (PGT-M et PGT-SR) reste accessible en CPDPN sur indication. Sans test, la note morphologique reste l'indicateur clé dont dispose ta clinique pour choisir l'embryon à transférer en premier.",
      },
    ],
    references: [
      {
        title: "Blastocyst score affects implantation and pregnancy outcome",
        authors: "Gardner DK, Lane M, Stevens J, Schlenker T, Schoolcraft WB.",
        journal: "Fertility and Sterility",
        year: 2000,
        url: "https://www.fertstert.org/article/S0015-0282(00)00518-5/fulltext",
        evidenceLevel: "cohort",
      },
      {
        title:
          "The Istanbul consensus workshop on embryo assessment: proceedings of an expert meeting",
        authors:
          "Alpha Scientists in Reproductive Medicine and ESHRE Special Interest Group of Embryology",
        journal: "Human Reproduction",
        year: 2011,
        url: "https://academic.oup.com/humrep/article/26/6/1270/2913905",
        evidenceLevel: "guideline",
      },
      {
        title:
          "Correlation between standard blastocyst morphology, euploidy and implantation: an observational study in two centers",
        authors: "Capalbo A, Rienzi L, Cimadomo D, et al.",
        journal: "Human Reproduction",
        year: 2014,
        url: "https://academic.oup.com/humrep/article/29/6/1173/620894",
        evidenceLevel: "cohort",
      },
      {
        title:
          "Le diagnostic préimplantatoire (DPI) en France — cadre et indications",
        authors: "Agence de la biomédecine",
        url: "https://www.agence-biomedecine.fr/fr/diagnostic-preimplantatoire/diagnostic-preimplantatoire",
        evidenceLevel: "official",
      },
    ],
  },
});

frMoonArticle("ivf-protocols", {
  title: "Les protocoles de FIV expliqués",
  subtitle: "Antagoniste, long Lupron, mini-FIV — quel protocole et pourquoi.",
  body: {
    introduction: "Pour aller plus loin",
    quickSummary: [
      {
        title: "Ton protocole est personnalisé.",
        description:
          "Il est choisi en fonction de ta réserve ovarienne, ton âge, et ta réponse antérieure à la stimulation — il n'y a pas de modèle unique.",
      },
      {
        title: "Le protocole antagoniste est le plus courant aujourd'hui.",
        description:
          "Il est plus court, demande moins d'injections, et présente un risque plus faible de syndrome d'hyperstimulation ovarienne (SHO).",
      },
      {
        title: "Il n'y a pas un seul « meilleur » protocole.",
        description:
          "Le bon dépend de ta biologie spécifique. Si un cycle ne se passe pas comme espéré, ton médecin peut ajuster le protocole pour la fois suivante.",
      },
    ],
    sections: [
      {
        title: "Ce qu'est un protocole",
        content:
          "Un protocole de FIV est la combinaison spécifique de médicaments, de dosages et de timings que ton médecin prescrit pour la stimulation ovarienne. L'objectif est de faire grandir plusieurs follicules en toute sécurité (chacun contenant un ovocyte) tout en prévenant une ovulation prématurée. Différents protocoles utilisent différentes stratégies pour atteindre cet équilibre.",
      },
      {
        title: "Protocole antagoniste",
        content:
          "Le protocole antagoniste est l'approche la plus largement utilisée aujourd'hui. Tu commences les injections de gonadotrophines (médicaments à base de FSH comme Gonal-F ou Follistim) au 2e ou 3e jour du cycle, et tu ajoutes un antagoniste de la GnRH (Cetrotide ou Ganirelix) vers le jour 5 ou 6 pour prévenir une ovulation prématurée. Toute la phase de stimulation dure 8 à 12 jours.",
        bulletPoints: [
          "Protocole le plus courant en FIV moderne",
          "Phase de stimulation plus courte (8 à 12 jours)",
          "Risque plus faible de syndrome d'hyperstimulation ovarienne (SHO)",
          "Permet d'utiliser un déclenchement Lupron au lieu d'hCG, ce qui réduit encore le risque de SHO",
          "Idéal pour : la plupart des patientes, surtout celles avec une réserve ovarienne normale à élevée",
        ],
      },
      {
        title: "Protocole agoniste long (Lupron)",
        content:
          "Aussi appelé protocole long Lupron ou de blocage. Tu commences les injections de Lupron en phase lutéale du cycle précédant ta FIV (environ une semaine avant tes règles). Le Lupron stimule d'abord puis bloque ton hypophyse, donnant au médecin plus de contrôle sur le timing de la stimulation. Les gonadotrophines sont ajoutées une fois le blocage confirmé.",
        bulletPoints: [
          "Plus long — le Lupron commence environ 1 semaine avant tes règles",
          "Plus d'injections au total, mais une stimulation très contrôlée",
          "Peut produire une croissance des follicules plus synchronisée",
          "Risque de SHO plus élevé qu'avec le protocole antagoniste",
          "Idéal pour : les patientes avec endométriose ou celles qui ont besoin d'une stimulation très contrôlée",
        ],
      },
      {
        title: "Mini-FIV ou FIV à stimulation minimale",
        content:
          "La mini-FIV utilise des doses plus faibles de médicaments — souvent des médicaments oraux comme le Clomid ou le létrozole combinés à de petites doses de gonadotrophines. L'objectif est de recueillir moins d'ovocytes mais potentiellement de meilleure qualité. C'est plus doux pour le corps. En France, ce protocole est moins courant dans le parcours AMP conventionné, car la prise en charge à 100 % permet déjà des stimulations classiques. Aux États-Unis ou hors parcours remboursé, la mini-FIV est aussi attractive parce que moins chère par cycle, mais peut nécessiter plus de cycles pour obtenir le même nombre d'ovocytes.",
        bulletPoints: [
          "Doses de médicaments plus faibles, souvent en incluant des médicaments oraux",
          "Moins d'ovocytes recueillis par cycle (généralement 3 à 7)",
          "Coût par cycle plus faible, mais peut nécessiter plusieurs cycles",
          "Risque réduit de SHO",
          "Idéal pour : les patientes qui veulent une approche plus douce, celles avec une réserve ovarienne basse, ou pour qui le coût est un facteur majeur",
        ],
      },
      {
        title: "Lupron microdose flare",
        content:
          "Ce protocole utilise de très petites doses de Lupron au début de la stimulation pour créer un bref « pic » de FSH et LH depuis l'hypophyse, boostant la production hormonale propre du corps avant d'ajouter des gonadotrophines. Il est spécifiquement conçu pour les patientes qui ont mal répondu à d'autres protocoles.",
        bulletPoints: [
          "Utilise la décharge hormonale du corps pour amplifier la stimulation",
          "Conçu pour les répondeuses faibles ou avec réserve ovarienne diminuée",
          "Commence au jour 2 du cycle avec du Lupron à microdose",
          "Gonadotrophines ajoutées au jour 3 ou 4 du cycle",
        ],
      },
      {
        title: "FIV en cycle naturel",
        content:
          "La FIV en cycle naturel n'utilise aucun médicament de stimulation — ou un soutien très minimal. Ton médecin surveille ton cycle naturel et prélève le seul ovocyte que ton corps produit. Les taux de réussite par cycle sont plus faibles, mais il n'y a pas d'effets secondaires de stimulation et le coût par cycle est significativement réduit. Certaines patientes préfèrent cette approche pour des raisons personnelles ou médicales.",
      },
      {
        title: "Comment les médecins choisissent ton protocole",
        content:
          "Ton médecin considère plusieurs facteurs au moment de choisir un protocole : ton âge, ton taux d'AMH, ton comptage des follicules antraux, ton IMC, ton historique de FIV (le cas échéant), et toute condition comme le PMOS (ex SOPK) ou l'endométriose. Si c'est ton premier cycle, le choix est basé sur une prédiction. Si tu as déjà fait un cycle, ta réponse à ce protocole guide fortement le suivant.",
      },
      {
        title: "Changer de protocole entre les cycles",
        content:
          "Il est courant de changer de protocole entre les cycles. Si ton premier cycle a produit moins d'ovocytes que prévu, ton médecin peut essayer un autre protocole — doses plus élevées, autre stratégie de blocage, ou ajout de médicaments adjuvants comme l'hormone de croissance ou l'Omnitrope. Chaque cycle apporte des données qui aident à affiner l'approche. Un protocole différent ne signifie pas que le premier a échoué — cela signifie que ton médecin personnalise tes soins.",
      },
    ],
    references: [
      {
        title:
          "Gonadotrophin-releasing hormone antagonists for assisted reproductive technology",
        authors:
          "Al-Inany HG, Youssef MA, Ayeleke RO, Brown J, Lam WS, Broekmans FJ.",
        journal: "Cochrane Database of Systematic Reviews",
        year: 2016,
        url: "https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD001750.pub4/full",
        evidenceLevel: "meta-analysis",
      },
      {
        title: "ESHRE guideline: ovarian stimulation for IVF/ICSI",
        authors: "ESHRE Guideline Group on Ovarian Stimulation",
        journal: "Human Reproduction Open",
        year: 2020,
        url: "https://academic.oup.com/hropen/article/2020/2/hoaa009/5826933",
        evidenceLevel: "guideline",
      },
      {
        title:
          'ESHRE consensus on the definition of "poor response" to ovarian stimulation following IVF: the Bologna criteria',
        authors:
          "Ferraretti AP, La Marca A, Fauser BC, Tarlatzis B, Nargund G, Gianaroli L.",
        journal: "Human Reproduction",
        year: 2011,
        url: "https://academic.oup.com/humrep/article/26/7/1616/2914264",
        evidenceLevel: "review",
      },
    ],
  },
});

frMoonArticle("egg-freezing-timeline", {
  title: "Le calendrier de la congélation d'ovocytes",
  subtitle:
    "Une vue semaine par semaine de ce à quoi t'attendre pendant la congélation d'ovocytes.",
  body: {
    introduction: "Pour commencer",
    quickSummary: [
      {
        title: "Le processus actif dure environ 2 semaines.",
        description:
          "De ta première injection au jour de la ponction, la phase de stimulation dure 10 à 14 jours.",
      },
      {
        title: "Les examens et la consultation viennent en premier.",
        description:
          "Avant de commencer les injections, il te faudra 2 à 4 semaines d'examens initiaux, prises de sang, et une consultation avec ton médecin.",
      },
      {
        title: "La récupération est rapide.",
        description:
          "La plupart des personnes reprennent leurs activités normales en 2 à 3 jours après la ponction.",
      },
    ],
    sections: [
      {
        title: "Semaines précédentes : consultation et examens",
        content:
          "Le processus commence bien avant les injections. Ta première étape est une consultation initiale avec un médecin de la reproduction, suivie d'examens de base — prise de sang (AMH, FSH, œstradiol, TSH) et une échographie endovaginale pour compter tes follicules antraux. Certaines cliniques peuvent tout faire en une seule visite ; d'autres l'étalent sur 1 ou 2 rendez-vous.",
        bulletPoints: [
          "Consultation initiale avec ton médecin (en personne ou en visio)",
          "Prise de sang : AMH, FSH, œstradiol, TSH, dépistage des maladies infectieuses",
          "Échographie endovaginale à J2-J3 avec comptage des follicules antraux (CFA)",
          "Revue des résultats et finalisation de ton protocole de stimulation",
          "Commande des médicaments (prévoir 3 à 5 jours pour la livraison de la pharmacie)",
        ],
      },
      {
        title: "Semaine 1 de stimulation : début des injections",
        content:
          "Tu commenceras les injections au jour 2 ou 3 de tes règles. Les premiers jours impliquent généralement une injection par soir — une gonadotrophine comme Gonal-F ou Follistim. Vers le 4e ou 5e jour, ton médecin peut ajouter une seconde injection (un antagoniste de la GnRH comme le Ganirelix) pour prévenir une ovulation prématurée. Ta première visite de suivi aura lieu vers le 4e ou 5e jour.",
        bulletPoints: [
          "Jour 2 à 3 : début des injections de gonadotrophines",
          "Jour 4 à 5 : première visite de suivi (prise de sang + échographie)",
          "Jour 5 à 6 : un second médicament (antagoniste) peut être ajouté",
          "Les injections sont sous-cutanées — petites aiguilles, à s'administrer à la maison",
        ],
      },
      {
        title: "Semaine 2 de stimulation : suivi et déclenchement",
        content:
          "Les visites de suivi passent à toutes les 1 à 2 journées pendant la semaine 2. Ton médecin suit la croissance des follicules par échographie et ajuste les doses de médicaments selon ta réponse. Quand les follicules dominants atteignent 18 à 20 mm, ton médecin programmera le déclenchement de l'ovulation — une injection au timing précis qui provoque la maturation finale des ovocytes exactement 36 heures avant la ponction.",
        bulletPoints: [
          "Visites de suivi toutes les 1 à 2 journées",
          "Les doses de médicaments peuvent être ajustées à la hausse ou à la baisse",
          "Le timing du déclenchement est précis — souvent en fin de soirée pour une ponction le matin",
          "Tu arrêteras tous les autres médicaments après le déclenchement",
        ],
      },
      {
        title: "Le jour de la ponction",
        content:
          "La ponction ovocytaire est une procédure ambulatoire courte réalisée sous sédation légère — tu seras endormie mais n'auras pas besoin d'anesthésie générale. Guidé par échographie, le médecin insère une fine aiguille à travers la paroi vaginale pour aspirer le liquide de chaque follicule. La procédure dure 15 à 25 minutes. Tu te reposeras en salle de récupération pendant environ une heure avant de rentrer. Tu auras besoin de quelqu'un pour te ramener.",
        bulletPoints: [
          "Arrive à la clinique 30 à 60 minutes avant ton horaire programmé",
          "La procédure elle-même dure 15 à 25 minutes",
          "Sédation légère — tu ne sentiras rien pendant la procédure",
          "Tu recevras ton nombre d'ovocytes avant de quitter la clinique",
          "Organise quelqu'un pour te ramener à la maison",
        ],
      },
      {
        title: "Récupération : jours 1 à 3",
        content:
          "La plupart des personnes se sentent ballonnées, ont des crampes, et sont fatiguées le jour de la ponction et le lendemain. Des antidouleurs en vente libre (paracétamol, pas d'ibuprofène) suffisent généralement. Aux jours 2 à 3, la plupart se sentent beaucoup mieux et reprennent leurs activités normales. Évite les exercices intenses pendant environ une semaine.",
        bulletPoints: [
          "Jour 0 : repose-toi à la maison, attends-toi à des ballonnements et de légères crampes",
          "Jour 1 : la plupart se sentent nettement mieux",
          "Jour 2 à 3 : la plupart reprennent le travail et leurs activités normales",
          "Appelle ton médecin si tu ressens une douleur intense, des vomissements, une difficulté à respirer, ou un débit urinaire très faible — ce peuvent être des signes de SHO",
        ],
      },
      {
        title: "Après : conservation et étapes suivantes",
        content:
          "Tes ovocytes sont vitrifiés (congelés en flash) et stockés dans des cuves d'azote liquide. En France, l'autoconservation sociétale (sans raison médicale) se fait dans un centre public ou ESPIC, et la conservation est généralement gratuite ou avec une contribution modique selon le centre. En autoconservation médicale (préservation en cas de pathologie, chimiothérapie…), la conservation est intégrée à la prise en charge ALD. Aux États-Unis et dans les pays sans couverture publique, compte 500 à 1 000 $ par an. Il n'y a aucune preuve que la durée de conservation affecte la qualité des ovocytes. Quand tu seras prête à les utiliser, les ovocytes sont décongelés, fécondés avec du sperme par ICSI, et les embryons obtenus sont transférés.",
      },
    ],
    references: [
      {
        title: "ESHRE guideline: female fertility preservation",
        authors: "ESHRE Guideline Group on Female Fertility Preservation",
        journal: "Human Reproduction Open",
        year: 2020,
        url: "https://academic.oup.com/hropen/article/2020/4/hoaa052/5895297",
        evidenceLevel: "guideline",
      },
      {
        title:
          "Successful elective and medically indicated oocyte vitrification and warming for autologous IVF",
        authors: "Doyle JO, Richter KS, Lim J, et al.",
        journal: "Fertility and Sterility",
        year: 2016,
        url: "https://www.fertstert.org/article/S0015-0282(15)02011-5/fulltext",
        evidenceLevel: "cohort",
      },
      {
        title: "Autoconservation des ovocytes en France — cadre et parcours",
        authors: "Agence de la biomédecine",
        url: "https://www.procreation-medicale.fr/parcours-congeler-ovocytes/",
        evidenceLevel: "official",
      },
      {
        title: "Loi de bioéthique 2021 — autoconservation sociétale",
        authors: "Légifrance",
        year: 2021,
        url: "https://www.legifrance.gouv.fr/loda/id/JORFTEXT000043884384/",
        evidenceLevel: "official",
      },
    ],
  },
});

frMoonArticle("first-appointment", {
  title: "Que se passe-t-il à ton premier rendez-vous",
  subtitle: "À quoi t'attendre, quoi apporter, et quelles questions poser.",
  body: {
    introduction: "Pour commencer",
    quickSummary: [
      {
        title: "C'est un rendez-vous pour faire le point.",
        description:
          "Aucun examen invasif le premier jour — ton médecin cherche à comprendre ton parcours, tes objectifs, et à esquisser un premier plan.",
      },
      {
        title: "Apporte ton dossier médical.",
        description:
          "Informations d'assurance, résultats d'examens antérieurs, et une liste de questions. Plus tu es préparée, plus le rendez-vous sera utile.",
      },
      {
        title: "Tu repartiras avec un plan.",
        description:
          "Attends-toi à une feuille de route préliminaire et probablement des prescriptions de prises de sang pour lancer le diagnostic.",
      },
    ],
    sections: [
      {
        title: "À quoi t'attendre",
        content:
          "Ton premier rendez-vous dure généralement 45 à 60 minutes. Tu rencontreras un médecin de la reproduction — un spécialiste de la médecine de la fertilité. Le rendez-vous est un échange : ton médecin veut comprendre ton historique médical, depuis combien de temps tu essaies (le cas échéant), et quels sont tes objectifs. Aucun examen invasif le premier jour.",
        bulletPoints: [
          "Prévois 45 à 60 minutes, paperasse d'admission comprise",
          "Tu rencontreras un médecin de la reproduction, pas un gynécologue généraliste",
          "Le rendez-vous est une conversation — pas d'aiguilles ni d'examens",
          "Certaines cliniques proposent des premières consultations en visio",
        ],
      },
      {
        title: "Quoi apporter",
        content:
          "Viens préparée pour tirer le maximum de ton rendez-vous. Si tu as un partenaire, c'est utile (mais pas obligatoire) qu'il t'accompagne. Beaucoup de cliniques envoient des formulaires d'admission à l'avance — remplis-les soigneusement avant d'arriver.",
        bulletPoints: [
          "Carte vitale et pièce d'identité",
          "Tout résultat antérieur de prise de sang, échographie ou chirurgie",
          "La liste de tes médicaments et compléments actuels",
          "Ton partenaire si possible — certaines questions vous concernent tous les deux",
          "Une liste écrite de tes questions (tu vas les oublier sinon)",
        ],
      },
      {
        title: "La revue de l'historique médical",
        content:
          "Ton médecin passera en revue ton historique de cycles menstruels, tes grossesses antérieures (le cas échéant), ton historique chirurgical, l'historique médical familial, et tes habitudes de vie. Sois honnête et exhaustive — les détails sur la durée de tes cycles, leurs irrégularités, des règles douloureuses, ou des diagnostics passés comme le PMOS (ex SOPK) ou l'endométriose aident ton médecin à planifier le bon bilan.",
      },
      {
        title: "Les premiers examens prescrits",
        content:
          "Selon ton historique, ton médecin prescrira une série d'examens de base. Ils sont généralement programmés au 2e ou 3e jour de tes prochaines règles. L'objectif est d'évaluer ta réserve ovarienne, tes niveaux hormonaux, et ton anatomie reproductive.",
        bulletPoints: [
          "Prise de sang : AMH, FSH, œstradiol, TSH, prolactine",
          "Échographie endovaginale à J2-J3 avec comptage des follicules antraux (CFA)",
          "Spermogramme pour le partenaire masculin — souvent fait lors d'un rendez-vous séparé",
          "Des examens supplémentaires comme une HSG ou une hystérosonographie peuvent être recommandés",
        ],
      },
      {
        title: "Questions à poser à ton médecin",
        content:
          "Ce premier rendez-vous est aussi ta chance d'évaluer ton médecin, autant qu'il t'évalue. N'hésite pas — un bon médecin accueille les questions et prend le temps d'y répondre.",
        bulletPoints: [
          "D'après mon historique, qu'est-ce qui se passe selon vous ?",
          "Quels examens recommandez-vous et pourquoi ?",
          "Quelles options de traitement envisagez-vous pour moi ?",
          "Quels sont les taux de réussite pour quelqu'un dans ma situation ?",
          "Comment votre clinique gère-t-elle la communication et les résultats ?",
          "Quel est le délai d'ici à un plan de traitement ?",
        ],
      },
      {
        title: "Ce qui se passe ensuite",
        content:
          "Après les premiers examens (généralement en un cycle), tu auras un rendez-vous de suivi pour revoir les résultats et discuter d'un plan concret. C'est à ce moment-là que ton médecin recommandera une voie spécifique — surveillance, médicaments, IIU, FIV, ou examens diagnostiques supplémentaires. Le premier rendez-vous met tout en mouvement.",
      },
    ],
    references: [
      {
        title:
          "Diagnostic evaluation of the infertile female: a committee opinion",
        authors: "Practice Committee of the ASRM",
        journal: "Fertility and Sterility",
        year: 2021,
        url: "https://www.asrm.org/practice-guidance/practice-committee-documents/diagnostic-evaluation-of-the-infertile-female-a-committee-opinion-2021/",
        evidenceLevel: "guideline",
      },
      {
        title: "Fertility problems: assessment and treatment (CG156)",
        authors: "NICE",
        year: 2017,
        url: "https://www.nice.org.uk/guidance/cg156",
        evidenceLevel: "guideline",
      },
      {
        title:
          "Recommandations pour la pratique clinique : prise en charge du couple infertile",
        authors:
          "Collège national des gynécologues et obstétriciens français (CNGOF)",
        url: "https://www.cngof.fr/pratiques-cliniques/recommandations-pour-la-pratique-clinique",
        evidenceLevel: "guideline",
      },
    ],
  },
});
